import { DCUIContent } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import {
    DCUI_DISPLAYABLE_ATTRIBUTE_IDS,
    DCUI_DL_ATTRIBUTE_TYPE_ID,
    DCUI_IDCARD_ATTRIBUTE_TYPE_ID,
    DCUI_PASSPORT_ATTRIBUTE_TYPE_ID,
    DCUI_SELFIE_ATTRIBUTE_TYPE_ID,
    DCUI_VERSION_PROPERTY_TYPE_ID,
    TMX_IN_DCUI_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { DCUICaptureStepTypes } from "@enums/dcui-capture-step-types";
import { DCUIDocumentSide } from "@enums/dcui-document-side";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { DCUICaptureStep } from "@idw-types/common/dcui/dcui-capture-step";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationAttribute, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";

export const getCaptureStepStartInstructionsImageUrl = (captureStepType: DCUICaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case DCUICaptureStepTypes.FACE:
            return (content.face.useCustomStartInstructionImage && content.face.startInstructionsImageUrl)
                ? content.face.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/face.jpg`;
        case DCUICaptureStepTypes.LIVENESS_CHECK:
            return (content.face3d.useCustomStartInstructionImage && content.face3d.startInstructionsImageUrl)
                ? content.face3d.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/face.jpg`;
        case DCUICaptureStepTypes.DRIVER_LICENSE_FRONT:
            return (content.dlfront.useCustomStartInstructionImage && content.dlfront.startInstructionsImageUrl)
                ? content.dlfront.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/dlfront.jpg`;
        case DCUICaptureStepTypes.DRIVER_LICENSE_BACK:
            return (content.dlback.useCustomStartInstructionImage && content.dlback.startInstructionsImageUrl)
                ? content.dlback.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/dlback.jpg`;
        case DCUICaptureStepTypes.ID_CARD_FRONT:
            return (content.idfront.useCustomStartInstructionImage && content.idfront.startInstructionsImageUrl)
                ? content.idfront.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/idfront.jpg`;
        case DCUICaptureStepTypes.ID_CARD_BACK:
            return (content.idback.useCustomStartInstructionImage && content.idback.startInstructionsImageUrl)
                ? content.idback.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/idback.jpg`;
        case DCUICaptureStepTypes.PASSPORT:
            return (content.passport.useCustomStartInstructionImage && content.passport.startInstructionsImageUrl)
                ? content.passport.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/passport.jpg`;
        default:
            return "";
    }
};

export const getCaptureStepLabel = (captureStepType: DCUICaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case DCUICaptureStepTypes.FACE:
            return content.face.label;
        case DCUICaptureStepTypes.LIVENESS_CHECK:
            return content.face3d.label;
        case DCUICaptureStepTypes.DRIVER_LICENSE_FRONT:
            return content.dlfront.label;
        case DCUICaptureStepTypes.DRIVER_LICENSE_BACK:
            return content.dlback.label;
        case DCUICaptureStepTypes.ID_CARD_FRONT:
            return content.idfront.label;
        case DCUICaptureStepTypes.ID_CARD_BACK:
            return content.idback.label;
        case DCUICaptureStepTypes.PASSPORT:
            return content.passport.label;
        default:
            return "";
    }
};

export const getCaptureStepUrl = (captureStep: DCUICaptureStep): { url: string; side?: string } => {
    switch (captureStep.type) {
        case DCUICaptureStepTypes.FACE:
            return { url: '/face' };
        case DCUICaptureStepTypes.DRIVER_LICENSE_FRONT:
            return { url: '/driver-license', side: DCUIDocumentSide.FRONT };
        case DCUICaptureStepTypes.DRIVER_LICENSE_BACK:
            return { url: '/driver-license', side: DCUIDocumentSide.BACK };
        case DCUICaptureStepTypes.ID_CARD_FRONT:
            return { url: '/id-card', side: DCUIDocumentSide.FRONT };
        case DCUICaptureStepTypes.ID_CARD_BACK:
            return { url: '/id-card', side: DCUIDocumentSide.BACK };
        case DCUICaptureStepTypes.PASSPORT:
            return { url: '/passport' };
        case DCUICaptureStepTypes.LIVENESS_CHECK:
            return { url: '/liveness' };
        default:
            return null
    }
};

export const getCaptureStepInstructions = (captureStepType: DCUICaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case DCUICaptureStepTypes.FACE:
            return content.face.instructionsHtml;
        case DCUICaptureStepTypes.LIVENESS_CHECK:
            return content.face3d.instructionsHtml;
        case DCUICaptureStepTypes.DRIVER_LICENSE_FRONT:
            return content.dlfront.instructionsHtml;
        case DCUICaptureStepTypes.DRIVER_LICENSE_BACK:
            return content.dlback.instructionsHtml;
        case DCUICaptureStepTypes.ID_CARD_FRONT:
            return content.idfront.instructionsHtml;
        case DCUICaptureStepTypes.ID_CARD_BACK:
            return content.idback.instructionsHtml;
        case DCUICaptureStepTypes.PASSPORT:
            return content.passport.instructionsHtml;
        default:
            return "";
    }
};

export const getCaptureStepShowScreenOrientationPreview = (captureStepType: DCUICaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case DCUICaptureStepTypes.FACE:
            return content.face.showScreenOrientationPreview;
        case DCUICaptureStepTypes.LIVENESS_CHECK:
            return content.face3d.showScreenOrientationPreview;
        case DCUICaptureStepTypes.DRIVER_LICENSE_FRONT:
            return content.dlfront.showScreenOrientationPreview;
        case DCUICaptureStepTypes.DRIVER_LICENSE_BACK:
            return content.dlback.showScreenOrientationPreview;
        case DCUICaptureStepTypes.ID_CARD_FRONT:
            return content.idfront.showScreenOrientationPreview;
        case DCUICaptureStepTypes.ID_CARD_BACK:
            return content.idback.showScreenOrientationPreview;
        case DCUICaptureStepTypes.PASSPORT:
            return content.passport.showScreenOrientationPreview;
        default:
            return false;
    }
};

export const getCaptureStepNativeCameraButtonText = (captureStepType: DCUICaptureStepTypes, content: DCUIContent) => {
    let buttonText;
    switch (captureStepType) {
        case DCUICaptureStepTypes.FACE:
            buttonText = content.face.nativeCameraButtonText;
            break;
        case DCUICaptureStepTypes.LIVENESS_CHECK:
            buttonText = content.face3d.nativeCameraButtonText;
            break;
        case DCUICaptureStepTypes.DRIVER_LICENSE_FRONT:
            buttonText = content.dlfront.nativeCameraButtonText;
            break;
        case DCUICaptureStepTypes.DRIVER_LICENSE_BACK:
            buttonText = content.dlback.nativeCameraButtonText;
            break;
        case DCUICaptureStepTypes.ID_CARD_FRONT:
            buttonText = content.idfront.nativeCameraButtonText;
            break;
        case DCUICaptureStepTypes.ID_CARD_BACK:
            buttonText = content.idback.nativeCameraButtonText;
            break;
        case DCUICaptureStepTypes.PASSPORT:
            buttonText = content.passport.nativeCameraButtonText;
            break;
        default:
            buttonText = content.general.nativeCameraButtonText;
            break;
    }
    return buttonText || content.general.nativeCameraButtonText;
};

export const getCaptureStepScreenOrientationPreviewMessage = (captureStepType: DCUICaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case DCUICaptureStepTypes.FACE:
            return content.face.screenOrientationPreviewText;
        case DCUICaptureStepTypes.LIVENESS_CHECK:
            return content.face3d.screenOrientationPreviewText;
        case DCUICaptureStepTypes.DRIVER_LICENSE_FRONT:
            return content.dlfront.screenOrientationPreviewText;
        case DCUICaptureStepTypes.DRIVER_LICENSE_BACK:
            return content.dlback.screenOrientationPreviewText;
        case DCUICaptureStepTypes.ID_CARD_FRONT:
            return content.idfront.screenOrientationPreviewText;
        case DCUICaptureStepTypes.ID_CARD_BACK:
            return content.idback.screenOrientationPreviewText;
        case DCUICaptureStepTypes.PASSPORT:
            return content.passport.screenOrientationPreviewText;
        default:
            return "";
    }
};

export const getCaptureStepScreenOrientationMediaUrl = (captureStepType: DCUICaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case DCUICaptureStepTypes.FACE:
            return (content.face.useCustomScreenOrientationVideo && content.face.screenOrientationMediaUrl)
                ? content.face.screenOrientationMediaUrl
                : content.face.screenOrientationVideoUrl;
        case DCUICaptureStepTypes.LIVENESS_CHECK:
            return (content.face3d.useCustomScreenOrientationVideo && content.face3d.screenOrientationMediaUrl)
                ? content.face3d.screenOrientationMediaUrl
                : content.face3d.screenOrientationVideoUrl;
        case DCUICaptureStepTypes.DRIVER_LICENSE_FRONT:
            return (content.dlfront.useCustomScreenOrientationVideo && content.dlfront.screenOrientationMediaUrl)
                ? content.dlfront.screenOrientationMediaUrl
                : content.dlfront.screenOrientationVideoUrl;
        case DCUICaptureStepTypes.ID_CARD_FRONT:
            return (content.idfront.useCustomScreenOrientationVideo && content.idfront.screenOrientationMediaUrl)
                ? content.idfront.screenOrientationMediaUrl
                : content.idfront.screenOrientationVideoUrl;
        case DCUICaptureStepTypes.DRIVER_LICENSE_BACK:
            return (content.dlback.useCustomScreenOrientationVideo && content.dlback.screenOrientationMediaUrl)
                ? content.dlback.screenOrientationMediaUrl
                : content.dlback.screenOrientationVideoUrl;
        case DCUICaptureStepTypes.ID_CARD_BACK:
            return (content.idback.useCustomScreenOrientationVideo && content.idback.screenOrientationMediaUrl)
                ? content.idback.screenOrientationMediaUrl
                : content.idback.screenOrientationVideoUrl;
        case DCUICaptureStepTypes.PASSPORT:
            return (content.passport.useCustomScreenOrientationVideo && content.passport.screenOrientationMediaUrl)
                ? content.passport.screenOrientationMediaUrl
                : content.passport.screenOrientationVideoUrl;
        default:
            return "";
    }
};

export const getNextCaptureStepUrl = (captureSteps: Array<DCUICaptureStep>) => {
    let nextCaptureStep: { url: string; side?: string };
    const incompleteCaptureStep = captureSteps.find(captureStep => !captureStep.completed && !captureStep.optional && captureStep.method !== DCUICaptureStepTypes.NONE);
    if (incompleteCaptureStep) {
        nextCaptureStep = getCaptureStepUrl(incompleteCaptureStep);
    } else {
        nextCaptureStep = { url: "/complete" };
    }

    let nextCaptureStepUrl;
    if (nextCaptureStep?.url) {
        nextCaptureStepUrl = `/dcui${nextCaptureStep.url}`;
        if (nextCaptureStep.side) {
            nextCaptureStepUrl += `/${nextCaptureStep.side}`;
        }
    }

    return nextCaptureStepUrl;
};

export const removeDuplicatedDCUISteps = (steps: Array<DCUIThemingStep>) => {
    const stepTypeIDS: Array<DCUICaptureStepTypes> = [];
    return steps.reduce((result, step) => {
        if (!stepTypeIDS.includes(step.stepType)) {
            stepTypeIDS.push(step.stepType)
            result.push(step);
        }
        return result;
    }, []);
}

export const shouldUseDCUIVersion2 = (properties: Array<FormattedRpServiceConfigurationProperty>) => {
    return !!properties.find(property =>
        property.propertyTypeId === DCUI_VERSION_PROPERTY_TYPE_ID &&
        property.addedToService &&
        property.value === "2.0"
    );
};

export const isTmxInDCUIEnabled = (properties: Array<FormattedRpServiceConfigurationProperty>) => {
    return properties.some(property =>
        property.propertyTypeId === TMX_IN_DCUI_PROPERTY_TYPE_ID &&
        property.addedToService &&
        isStringTrue(property.value)
    );
}

export const isAttributeDisplayable = (selectedAttributes: Array<FormattedRpServiceConfigurationAttribute>) => {
    return selectedAttributes.some(attribute => {
        return (
            DCUI_DISPLAYABLE_ATTRIBUTE_IDS.includes(attribute.attributeType?.id) &&
            isStringTrue(attribute.isAsync)
        );
    });
}

export const getActiveDCUICaptureSteps = (rpServiceConfigurationAttributes: Array<FormattedRpServiceConfigurationAttribute>) => {
    const steps: Array<DCUIThemingStep> = [];
    rpServiceConfigurationAttributes
        .filter(attribute => attribute.addedToService && attribute.status === EntityStatusTypes.ACTIVE)
        .forEach(attr => {
            if (attr.attributeType?.id === DCUI_DL_ATTRIBUTE_TYPE_ID) {
                steps.push({
                    stepType: DCUICaptureStepTypes.DRIVER_LICENSE_FRONT,
                    tabType: DCUIThemingTabTypes.DL_FRONT,
                    stepResources: resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.driverLicenseFront
                }, {
                    stepType: DCUICaptureStepTypes.DRIVER_LICENSE_BACK,
                    tabType: DCUIThemingTabTypes.DL_BACK,
                    stepResources: resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.driverLicenseBack
                });
            } else if (attr.attributeType?.id === DCUI_IDCARD_ATTRIBUTE_TYPE_ID) {
                steps.push({
                    stepType: DCUICaptureStepTypes.ID_CARD_FRONT,
                    tabType: DCUIThemingTabTypes.ID_FRONT,
                    stepResources: resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.idCardFront
                }, {
                    stepType: DCUICaptureStepTypes.ID_CARD_BACK,
                    tabType: DCUIThemingTabTypes.ID_BACK,
                    stepResources: resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.idCardBack
                });
            } else if (attr.attributeType?.id === DCUI_SELFIE_ATTRIBUTE_TYPE_ID) {
                steps.push({
                    stepType: DCUICaptureStepTypes.FACE,
                    tabType: DCUIThemingTabTypes.SELFIE,
                    stepResources: resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.selfie
                });
            } else if (attr.attributeType?.id === DCUI_PASSPORT_ATTRIBUTE_TYPE_ID) {
                steps.push({
                    stepType: DCUICaptureStepTypes.PASSPORT,
                    tabType: DCUIThemingTabTypes.PASSPORT,
                    stepResources: resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.passport
                });
            }
        });
    return steps;
}


export const getActiveWorkflowDCUICaptureSteps = (rpServiceConfigurations: Array<FormattedRpServiceConfiguration>) => {
    let steps: Array<DCUIThemingStep> = [];
    let shouldActivateDCUITab = false;

    rpServiceConfigurations.forEach(rpServiceConfig => {
        const { properties, attributes } = rpServiceConfig;

        const selectedAttributes = attributes.filter(attr => attr.addedToService && attr.status === EntityStatusTypes.ACTIVE);
        const activeProperties = properties.filter(attr => attr.addedToService);

        if (!shouldActivateDCUITab) {
            shouldActivateDCUITab = shouldUseDCUIVersion2(activeProperties);
        }

        if (shouldActivateDCUITab) {
            const isDisplayable = isAttributeDisplayable(selectedAttributes);
            const isTmxInDCUIEnable = isTmxInDCUIEnabled(activeProperties);

            const hasDisplayableDocumentCaptureAttributes = isDisplayable || isTmxInDCUIEnable;

            if (hasDisplayableDocumentCaptureAttributes) {
                steps = [...steps, ...getActiveDCUICaptureSteps(selectedAttributes)];
            }
        }
    });

    return removeDuplicatedDCUISteps(steps);
}